import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface Project {
  name: string;
  image: string;
  link: string;
  locked?: boolean;
}

interface NextProjectProps {
  currentProjectName: string;
  projects: Project[];
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const NextProject: React.FC<NextProjectProps> = ({ currentProjectName, projects, onVisibilityChange }) => {
  const [isVisible, setIsVisible] = useState(false);

  // Find current project index
  const currentIndex = projects.findIndex(project => {
    const normalizedProjectName = project.name.toLowerCase().trim().replace(/\s+/g, '-');
    const normalizedCurrentName = currentProjectName.toLowerCase().trim().replace(/\s+/g, '-');
    // Also try matching without normalization for exact matches
    return normalizedProjectName === normalizedCurrentName || 
           project.name.toLowerCase().trim() === currentProjectName.toLowerCase().trim();
  });
  
  // Get next unlocked project (wrap around to first if at end)
  const getNextUnlockedProject = () => {
    if (currentIndex === -1) {
      // If current project not found, find first unlocked project
      return projects.find(project => !project.locked) || projects[0];
    }
    
    // Start from next index and wrap around
    for (let i = 1; i < projects.length; i++) {
      const nextIndex = (currentIndex + i) % projects.length;
      const nextProject = projects[nextIndex];
      if (!nextProject.locked) {
        return nextProject;
      }
    }
    
    // Fallback to first project if all are locked
    return projects[0];
  };
  
  const nextProject = getNextUnlockedProject();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show component when user has scrolled past most of the content
      const threshold = documentHeight - windowHeight - 200; // 200px before bottom
      const newVisibility = scrollPosition > threshold;
      
      if (newVisibility !== isVisible) {
        setIsVisible(newVisibility);
        onVisibilityChange?.(newVisibility);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible, onVisibilityChange]);

  if (!nextProject) return null;

  return (
    <Link to={nextProject.link} className="block group">
      <motion.section 
        className={`w-full bg-[#0048FF] fixed bottom-0 left-0 z-[70] transition-transform duration-700 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] cursor-pointer ${
          isVisible ? 'translate-y-0' : 'translate-y-[200%] md:translate-y-full'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ 
              duration: 0.6,
              delay: 0.2,
              ease: [0.22, 1, 0.36, 1]
            }}
            className="flex items-center gap-6 lg:gap-12"
          >
            {/* Text content on the left */}
            <div className="flex-1">
              {/* Next Project text */}
              <p className="text-xs lg:text-sm [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-white opacity-80 mb-2 tracking-widest group-hover:opacity-100 transition-opacity duration-300">
                Next Project
              </p>

              {/* Project name */}
              <h2 className="text-xl lg:text-3xl xl:text-4xl [font-family:'ItemsTextTrial-Thin',Helvetica] font-thin text-white tracking-tight leading-tight group-hover:opacity-90 transition-opacity duration-300">
                {nextProject.name}
                {nextProject.locked && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="inline-block ml-2 opacity-60"
                  >
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                )}
              </h2>
            </div>

            {/* Image on the right */}
            <div className="flex-shrink-0">
              <div className="relative overflow-hidden rounded-lg w-24 h-24 lg:w-32 lg:h-32">
                <motion.img
                  src={nextProject.image}
                  alt={nextProject.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </Link>
  );
};
